import type { Filmtype, RateStructure, WebsiteType } from '@jywer/intake';
import type { ProductFormState, ProductKind } from './intake-form-state';
import { emptyProduct } from './intake-form-state';

const INPUT_CLS =
  'rounded border border-line-2 bg-paper-0 px-2 py-1 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

const KIND_LABEL: Record<ProductKind, string> = {
  website: 'Website',
  video: 'Video',
  pakkelosning: 'Pakkeløsning',
  klippekort: 'Klippekort',
};

const RATE_LABEL: Record<RateStructure, string> = {
  '1_rate': '1 rate',
  '2_rater_50_50': '2 rater 50/50',
  '3_rater': '3 rater',
  specialaftale: 'Specialaftale',
};

const WEBSITE_TYPE_LABEL: Record<WebsiteType, string> = {
  basic: 'Basic',
  template: 'Template',
  custom: 'Custom',
  ecommerce: 'E-commerce',
  other: 'Andet',
};

const FILMTYPE_LABEL: Record<Filmtype, string> = {
  profilvideo: 'Profilvideo',
  employer_branding: 'Employer branding',
  testimonial: 'Testimonial',
  produktvideo: 'Produktvideo',
  content_pakke: 'Content-pakke',
  anden_video: 'Anden video',
};

interface Props {
  product: ProductFormState;
  index: number;
  onChange: (next: ProductFormState) => void;
  onRemove?: () => void;
  /** When true, this row is rendered inside a pakkelosning child slot. */
  isChild?: boolean;
  fieldErrors?: Record<string, string | undefined>;
}

function FieldErr({ msg }: { msg: string | undefined }) {
  if (!msg) return null;
  return <p className="mt-0.5 text-xs text-status-err">{msg}</p>;
}

export function CreateOrderProductRow({
  product,
  index,
  onChange,
  onRemove,
  isChild = false,
  fieldErrors,
}: Props) {
  const set = <K extends keyof ProductFormState>(key: K, value: ProductFormState[K]) => {
    onChange({ ...product, [key]: value });
  };

  const KIND_OPTIONS: ProductKind[] = isChild
    ? ['website', 'video']
    : ['website', 'video', 'pakkelosning', 'klippekort'];

  return (
    <div
      className={`flex flex-col gap-3 rounded border ${
        isChild ? 'border-line-1 bg-paper-1' : 'border-line-2 bg-paper-0'
      } p-3`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-2">
          {isChild ? `Pakke-element ${index + 1}` : `Produkt ${index + 1}`}
        </span>
        <select
          value={product.kind}
          onChange={(e) =>
            onChange({ ...emptyProduct(e.target.value as ProductKind), title: product.title })
          }
          className={INPUT_CLS}
        >
          {KIND_OPTIONS.map((k) => (
            <option key={k} value={k}>
              {KIND_LABEL[k]}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={product.title}
          onChange={(e) => set('title', e.target.value)}
          placeholder="Titel (valgfri)"
          className={`${INPUT_CLS} flex-1`}
        />
        {onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className="rounded border border-status-err px-2 py-1 text-xs text-status-err hover:bg-status-err/5"
          >
            Fjern
          </button>
        ) : null}
      </div>

      {product.kind === 'website' ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            Pris (DKK)
            <input
              type="text"
              inputMode="decimal"
              value={product.websitePrice}
              onChange={(e) => set('websitePrice', e.target.value)}
              className={INPUT_CLS}
            />
            <FieldErr msg={fieldErrors?.['website_price']} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            Type
            <select
              value={product.websiteType}
              onChange={(e) => set('websiteType', e.target.value as WebsiteType)}
              className={INPUT_CLS}
            >
              {(Object.keys(WEBSITE_TYPE_LABEL) as WebsiteType[]).map((k) => (
                <option key={k} value={k}>
                  {WEBSITE_TYPE_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            Ratestruktur
            <select
              value={product.rateStructure}
              onChange={(e) => set('rateStructure', e.target.value as RateStructure)}
              className={INPUT_CLS}
            >
              {(Object.keys(RATE_LABEL) as RateStructure[]).map((k) => (
                <option key={k} value={k}>
                  {RATE_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {product.kind === 'video' ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            Pris (DKK)
            <input
              type="text"
              inputMode="decimal"
              value={product.videoPrice}
              onChange={(e) => set('videoPrice', e.target.value)}
              className={INPUT_CLS}
            />
            <FieldErr msg={fieldErrors?.['video_price']} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            Filmtype
            <select
              value={product.filmtype}
              onChange={(e) => set('filmtype', e.target.value as Filmtype | '')}
              className={INPUT_CLS}
            >
              <option value="">— vælg —</option>
              {(Object.keys(FILMTYPE_LABEL) as Filmtype[]).map((k) => (
                <option key={k} value={k}>
                  {FILMTYPE_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            Ratestruktur
            <select
              value={product.rateStructure}
              onChange={(e) => set('rateStructure', e.target.value as RateStructure)}
              className={INPUT_CLS}
            >
              {(Object.keys(RATE_LABEL) as RateStructure[]).map((k) => (
                <option key={k} value={k}>
                  {RATE_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
          {product.filmtype === 'anden_video' ? (
            <label className="md:col-span-3 flex flex-col gap-1 text-xs text-ink-2">
              Beskrivelse af video (titel)
              <input
                type="text"
                value={product.filmtypeOther}
                onChange={(e) => set('filmtypeOther', e.target.value)}
                placeholder="Fx 'Reklame til Black Friday'"
                className={INPUT_CLS}
              />
            </label>
          ) : null}
          <label className="md:col-span-3 flex flex-col gap-1 text-xs text-ink-2">
            Lokation(er) — fri tekst (fx adresser separeret med komma)
            <input
              type="text"
              value={product.videoLocations}
              onChange={(e) => set('videoLocations', e.target.value)}
              className={INPUT_CLS}
            />
          </label>
          <div className="md:col-span-3">
            <span className="block text-xs text-ink-2">Optagelsesdage</span>
            <div className="mt-1 flex flex-wrap gap-2">
              {product.shootDates.map((d, i) => (
                <div key={i} className="flex items-center gap-1">
                  <input
                    type="date"
                    value={d}
                    onChange={(e) => {
                      const next = [...product.shootDates];
                      next[i] = e.target.value;
                      set('shootDates', next);
                    }}
                    className={INPUT_CLS}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const next = product.shootDates.filter((_, idx) => idx !== i);
                      set('shootDates', next);
                    }}
                    className="text-xs text-ink-2 underline"
                  >
                    fjern
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => set('shootDates', [...product.shootDates, ''])}
                className="rounded border border-line-2 px-2 py-1 text-xs text-primary"
              >
                + Tilføj optagelsesdag
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {product.kind === 'klippekort' ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            Klippekort-pris (DKK)
            <input
              type="text"
              inputMode="decimal"
              value={product.klippekortPrice}
              onChange={(e) => set('klippekortPrice', e.target.value)}
              className={INPUT_CLS}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            Antal timer
            <input
              type="text"
              inputMode="decimal"
              value={product.totalHoursPurchased}
              onChange={(e) => set('totalHoursPurchased', e.target.value)}
              className={INPUT_CLS}
            />
          </label>
        </div>
      ) : null}

      {product.kind === 'pakkelosning' ? (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <label className="flex flex-col gap-1 text-xs text-ink-2">
              Samlet pakke-værdi (DKK)
              <input
                type="text"
                inputMode="decimal"
                value={product.pakkeTotalValue}
                onChange={(e) => set('pakkeTotalValue', e.target.value)}
                className={INPUT_CLS}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-ink-2">
              Ratestruktur
              <select
                value={product.rateStructure}
                onChange={(e) => set('rateStructure', e.target.value as RateStructure)}
                className={INPUT_CLS}
              >
                {(Object.keys(RATE_LABEL) as RateStructure[]).map((k) => (
                  <option key={k} value={k}>
                    {RATE_LABEL[k]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-ink-1">
              <input
                type="checkbox"
                checked={product.pakkeBilledAsSingleLine}
                onChange={(e) => set('pakkeBilledAsSingleLine', e.target.checked)}
              />
              Faktureres som én linje (ikke pr. delprodukt)
            </label>
          </div>
          <div className="rounded border border-line-1 bg-paper-1 p-2">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-2">
              Pakke-elementer
            </div>
            <div className="flex flex-col gap-2">
              {product.pakkeChildren.map((child, i) => (
                <CreateOrderProductRow
                  key={i}
                  product={child}
                  index={i}
                  isChild
                  onChange={(next) => {
                    const arr = [...product.pakkeChildren];
                    arr[i] = next;
                    set('pakkeChildren', arr);
                  }}
                  onRemove={() => {
                    const arr = product.pakkeChildren.filter((_, idx) => idx !== i);
                    set('pakkeChildren', arr);
                  }}
                />
              ))}
              <button
                type="button"
                onClick={() =>
                  set('pakkeChildren', [...product.pakkeChildren, emptyProduct('website')])
                }
                className="self-start rounded border border-line-2 px-2 py-1 text-xs text-primary"
              >
                + Tilføj pakke-element
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
